import ContentShell from "components/ContentShell";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useStyles from "styles/banner/bannerEditor";
import { SimpleTextInput } from "components/FormTextInput";
import {
  createBanner,
  getBannerInfo,
  updateBanner,
} from "services/homeBannerService";
import FilePicker from "components/FilePicker";
import * as Yup from "yup";
import {
  uploadImageV2,
  uploadContentTranslations,
} from "services/uploadService";
import { SimpleSelectInput } from "components/FormSelectInput";
import SearchableInput from "components/SearchableInput";
import BulkTranslationUpload from "components/BulkTranslationUpload";
import TranslationButtons from "components/TranslationButtons";
import useOpenClose from "hooks/useOpenClose";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import SimpleSwitch from "components/Switch";
import ColorPicker from "components/ColorPicker";
import sessionHelperInstance from "services/sessionHelper";
import { ImageType, DEFAULT_IMAGE_DIMENSIONS } from "utils/imageConstants";

interface BannerDetails {
  title: string;
  titleTrans: Record<string, any>;
  navigationPath: string;
  body: string;
  bodyTrans: Record<string, any>;
  fileType: string;
  isActive: boolean;
  order: any;
  textColor: string;
  isTitleBold: boolean;
  isBodyBold: boolean;
  territory: string;
  operators: any[];
  imageUrl?: string;
  archivedAt?: string;
}

const _bannerDetails: BannerDetails = {
  title: "",
  titleTrans: {},
  navigationPath: "STORE",
  body: "",
  bodyTrans: {},
  fileType: "",
  isActive: true,
  order: null,
  textColor: "#FFFFFF",
  isTitleBold: false,
  isBodyBold: false,
  territory: "all",
  operators: [],
};

const bannerValidation = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  navigationPath: Yup.string().required("Navigation Path is required"),
  body: Yup.string().required("Body is required"),
  textColor: Yup.string().required("Color is required"),
  order: Yup.number().positive().min(0).required("Order is required"),
});

const HomeBannerEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bannerDetails, setBannerDetails] = useState(_bannerDetails);
  const [image, setImage] = useState(null);
  const [imageDimensions, setImageDimensions] = useState<{
    targetWidth: number;
    targetHeight: number;
    originalWidth: number;
    originalHeight: number;
  } | null>(null);
  const [translationFile, setTranslationFile] = useState<File | null>(null);
  const { isOpen, close, open } = useOpenClose();
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });

  const cancel = () => navigate(configData.routes.homeBanners.homeBanners);

  const submit = async () => {
    try {
      setLoading(true);
      let response;
      await bannerValidation.validate(bannerDetails);

      if (id === "create") {
        response = await createBanner({
          ...bannerDetails,
          order: Number(bannerDetails?.order),
        });

        if (response?.data?.fileUploadUrl) {
          const fileUploadUrl = response?.data?.fileUploadUrl;
          await upload(fileUploadUrl);
        }

        if (translationFile && response?.data?._id) {
          await uploadContentTranslations(
            translationFile,
            "BANNER",
            response.data._id,
          );
        }
      } else {
        const { fileType, order, imageUrl, archivedAt, ...rest } =
          bannerDetails;
        if (fileType !== "") rest["fileType"] = fileType;

        response = await updateBanner({ ...rest, order: Number(order) }, id);

        if (bannerDetails.fileType !== "" && response?.data?.fileUploadUrl) {
          const fileUploadUrl = response?.data?.fileUploadUrl;
          await upload(fileUploadUrl);
        }
      }
      showSnackbar(response.message, "success");
      setLoading(false);
      navigate(configData.routes.homeBanners.homeBanners);
    } catch (error) {
      if (error?.inner)
        error.inner.forEach((error) => showSnackbar(error.message, "error"));
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;
    let data = {
      ...bannerDetails,
      [name]: ["isActive", "isTitleBold", "isBodyBold"].includes(name)
        ? !bannerDetails[name]
        : value,
    };
    if (["title", "body"].includes(name)) {
      data[`${name}Trans`]["en"] = value;
    }
    setBannerDetails((state) => ({ ...state, ...data }));
  };

  const upload = async (url) => {
    try {
      const defaultDims = DEFAULT_IMAGE_DIMENSIONS[ImageType.BANNER];
      const resp = await uploadImageV2({
        data: image,
        uploadUrl: url,
        resize: true,
        resizeToWidth: imageDimensions?.targetWidth || defaultDims.width,
        resizeToHeight: imageDimensions?.targetHeight || defaultDims.height,
      });

      if (resp.status === 200)
        showSnackbar(resp.message ?? "Image Uploaded", "success");
      return resp;
    } catch (error) {
      showSnackbar(error.message || "Error uploading image!");
    }
  };

  const onSelectImage = (image: any, dimensions?: any) => {
    setImage(image);
    if (dimensions) {
      setImageDimensions(dimensions);
    }
    setBannerDetails((state) => ({
      ...state,
      fileType: "image/webp",
    }));
  };

  const fetchBannerInfo = async () => {
    try {
      setLoading(true);
      const resp = await getBannerInfo(id);
      if (resp?.data) {
        let titleTrans = { ...resp.data?.titleTrans };
        let bodyTrans = { ...resp.data?.bodyTrans };

        if (resp?.data?.body && !bodyTrans["en"]) {
          bodyTrans["en"] = resp.data?.body;
        } else resp.data.body = bodyTrans["en"];
        if (resp?.data?.title && !titleTrans["en"]) {
          titleTrans["en"] = resp.data?.title;
        } else resp.data.title = titleTrans["en"];

        setBannerDetails({ ...resp.data, bodyTrans, titleTrans });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const getTranslatedData = (field: any) => {
    const _translatedData = Object.entries(bannerDetails?.[field]).map(
      ([key, value]) => ({
        lang: key,
        inputValue: value,
      }),
    );
    if (!_translatedData) return [];
    return _translatedData;
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error",
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setBannerDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce(
        (acc, key) => {
          if (key !== data.lang) {
            acc[key] = prev[modalData.field][key];
          }
          return acc;
        },
        {},
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setBannerDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    // if english translation is available set it field
    if (modalData.language === "en" && modalData.inputValue !== "") {
      setBannerDetails((details) => ({
        ...details,
        [field?.split("Trans")[0]]: modalData.inputValue,
        [field]: { ...details[field], en: modalData.inputValue },
      }));
    }
    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const handleOperatorChange = (e: any) => {
    const { value } = e.target;
    // Handle SearchableInput format (array of objects with id/label)
    if (Array.isArray(value)) {
      const operatorIds = value.map((item) =>
        typeof item === "object" ? item.id : item,
      );
      setBannerDetails((prev) => ({
        ...prev,
        operators: operatorIds,
      }));
    } else {
      // Handle other input formats
      setBannerDetails((prev) => ({
        ...prev,
        operators: value ? [value] : [],
      }));
    }
  };

  useEffect(() => {
    if (id !== "create") fetchBannerInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Home Banner`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Title"
              name="title"
              value={bannerDetails.title}
              onChange={onChange}
              button={
                <TranslationButtons
                  field={"titleTrans"}
                  onClick={handleOpenModal}
                />
              }
            />
          </Box>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="NavigationPath"
              name="navigationPath"
              value={bannerDetails.navigationPath}
              options={[{ id: "STORE", label: "Store" }]}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Body"
              name="body"
              value={bannerDetails.body}
              onChange={onChange}
              button={
                <TranslationButtons
                  field={"bodyTrans"}
                  onClick={handleOpenModal}
                />
              }
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Banner Order"
              name="order"
              type="number"
              value={bannerDetails.order}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Territory"
              name="territory"
              value={bannerDetails?.territory || ""}
              options={sessionHelperInstance.countryOptions}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field}>
            <SearchableInput
              selected={(bannerDetails.operators || []).map((operatorId) => {
                const operator = sessionHelperInstance.operatorOptions?.find(
                  (op) => op.id === operatorId,
                );
                return {
                  id: operatorId,
                  label: operator?.label || operatorId,
                };
              })}
              isMultiSelect={true}
              enableSelectAll={true}
              label={"Operators (Optional)"}
              name={"operators"}
              options={sessionHelperInstance.operatorOptions || []}
              onOptionSelect={handleOperatorChange}
              placeholder="Select operators (optional)"
              loading={loading}
            />
          </Box>
          <Box className={styles.field}>
            <ColorPicker
              label={"Text Color"}
              name="textColor"
              value={bannerDetails.textColor}
              onChange={onChange}
              customStyle={{ width: "100%", marginTop: 20 }}
            />
          </Box>
          <Box className={styles.field} style={{ alignSelf: "center" }}>
            <SimpleSwitch
              legendText={"Banner Status"}
              name="isActive"
              value={bannerDetails.isActive}
              label={bannerDetails.isActive ? "Active" : "Inactive"}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field} style={{ alignSelf: "center" }}>
            <SimpleSwitch
              legendText={"Title Style (Bold/Normal)"}
              name="isTitleBold"
              value={bannerDetails.isTitleBold}
              label={bannerDetails.isTitleBold ? "Bold" : "Normal"}
              onChange={onChange}
            />
          </Box>
          <Box className={styles.field} style={{ alignSelf: "center" }}>
            <SimpleSwitch
              legendText={"Body Style (Bold/Normal)"}
              name="isBodyBold"
              value={bannerDetails.isBodyBold}
              label={bannerDetails.isBodyBold ? "Bold" : "Normal"}
              onChange={onChange}
            />
          </Box>
        </Box>
        <Box className={styles.imagePickerContainer}>
          <FilePicker
            value={image}
            name="bannerImage"
            onFileSelect={onSelectImage}
            imageType={ImageType.BANNER}
            enableDimensionControl={true}
            metaData={{
              note: `Recommended: ${
                DEFAULT_IMAGE_DIMENSIONS[ImageType.BANNER].width
              } x ${
                DEFAULT_IMAGE_DIMENSIONS[ImageType.BANNER].height
              } pixels. Images will be resized and converted to WebP.`,
            }}
          />
          {bannerDetails?.imageUrl && !image && (
            <Box
              component="img"
              src={bannerDetails?.imageUrl}
              alt="Image Preview"
              className={styles.preview}
              style={{ width: "50%", marginTop: 20 }}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ mt: 3 }}>
        <BulkTranslationUpload
          assetType="BANNER"
          assetId={id !== "create" ? id : undefined}
          fields={[
            { label: "Title", fieldName: "titleTrans" },
            { label: "Body", fieldName: "bodyTrans" },
          ]}
          {...(id === "create"
            ? { deferUpload: true, onFileChange: setTranslationFile }
            : { onSuccess: fetchBannerInfo })}
        />
      </Box>
      <ViewLanguageModal
        isMultiline={true}
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignBody: "center",
            alignHead: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell>
  );
};

export default HomeBannerEditor;
